'use client';

import React, { useState, useCallback, useEffect, Suspense } from 'react';
import { Service, Booking, getActiveServices } from '@/lib/bookingStore';
import AgendarStepCalendar from '@/app/agendar/components/AgendarStepCalendar';
import AgendarStepTimeSlots from '@/app/agendar/components/AgendarStepTimeSlots';
import AgendarStepCustomer from '@/app/agendar/components/AgendarStepCustomer';
import AgendarStepSummary from '@/app/agendar/components/AgendarStepSummary';
import AgendarConfirmation from '@/app/agendar/components/AgendarConfirmation';

export interface AgendarState {
  selectedServices: Service[];
  selectedDate: string;
  selectedTime: string;
  customerName: string;
  customerWhatsapp: string;
  customerNotes: string;
}

const STEPS = [
  { num: 1, label: 'Data' },
  { num: 2, label: 'Horário' },
  { num: 3, label: 'Dados' },
  { num: 4, label: 'Confirmar' },
];

function BookingSectionInner({ preSelectedIds }: { preSelectedIds?: string[] }) {
  const [step, setStep] = useState(1);
  const [confirmedBooking, setConfirmedBooking] = useState<Booking | null>(null);
  const [state, setState] = useState<AgendarState>({
    selectedServices: [],
    selectedDate: '',
    selectedTime: '',
    customerName: '',
    customerWhatsapp: '',
    customerNotes: '',
  });

  useEffect(() => {
    const allServices = getActiveServices();
    if (preSelectedIds && preSelectedIds.length > 0) {
      const preSelected = allServices.filter(s => preSelectedIds.includes(s.id));
      if (preSelected.length > 0) {
        setState(prev => ({ ...prev, selectedServices: preSelected }));
        setStep(1);
      }
    } else if (preSelectedIds && preSelectedIds.length === 0) {
      setState(prev => ({ ...prev, selectedServices: [], selectedDate: '', selectedTime: '' }));
      setStep(1);
    }
  }, [preSelectedIds]);

  const update = useCallback((updates: Partial<AgendarState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const goNext = () => setStep(s => Math.min(s + 1, 4));
  const goBack = () => {
    if (step === 1) {
      const el = document.getElementById('servicos');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      setStep(s => Math.max(s - 1, 1));
    }
  };

  const handleScrollToServices = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById('servicos');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  if (confirmedBooking) {
    return (
      <section id="agendar" className="bg-ivory py-16">
        <AgendarConfirmation booking={confirmedBooking} />
      </section>
    );
  }

  return (
    <section id="agendar" className="bg-ivory">
      {/* Section header */}
      <div className="py-16 lg:py-20 border-b border-border bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="gold-line mx-auto mb-6 block" />
          <p className="text-xs tracking-[0.25em] uppercase font-medium text-burnt-rose mb-4">
            Marcação Online
          </p>
          <h2 className="font-serif text-4xl lg:text-5xl font-light leading-[1.1] text-dark-brown mb-4">
            Agende a sua <em className="italic text-burnt-rose">sessão</em>
          </h2>
          <p className="text-foreground/60 text-base leading-relaxed max-w-md mx-auto">
            Escolha os seus serviços na secção acima, selecione a data e hora e confirme a sua marcação em minutos.
          </p>
        </div>
      </div>

      {/* No services selected — prompt */}
      {state.selectedServices.length === 0 && (
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <div className="bg-white border border-border rounded-2xl p-10 shadow-sm">
            <div className="w-14 h-14 rounded-full bg-blush flex items-center justify-center mx-auto mb-5">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-burnt-rose">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <h3 className="font-serif text-2xl font-light text-dark-brown mb-3">
              Selecione os seus serviços
            </h3>
            <p className="text-foreground/60 text-sm leading-relaxed mb-6 max-w-sm mx-auto">
              Primeiro escolha os serviços que deseja na secção acima e depois volte aqui para escolher a data e hora.
            </p>
            <a
              href="#servicos"
              onClick={handleScrollToServices}
              className="inline-flex items-center justify-center px-8 py-3.5 btn-primary rounded-full text-xs tracking-[0.12em] uppercase font-semibold"
            >
              Ver Serviços
            </a>
          </div>
        </div>
      )}

      {/* Booking steps — only shown when services are selected */}
      {state.selectedServices.length > 0 && (
        <>
          {/* Selected services summary bar */}
          <div className="bg-blush/40 border-b border-border">
            <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground/50 uppercase tracking-wide">Serviços selecionados:</span>
                {state.selectedServices.map(s => (
                  <span key={s.id} className="text-xs bg-burnt-rose text-white px-2.5 py-1 rounded-full font-medium">
                    {s.name}
                  </span>
                ))}
              </div>
              <a
                href="#servicos"
                onClick={handleScrollToServices}
                className="text-xs text-burnt-rose underline underline-offset-2 hover:text-dark-brown transition-colors whitespace-nowrap"
              >
                Alterar serviços
              </a>
            </div>
          </div>

          {/* Step indicator */}
          <div className="border-b border-border bg-white">
            <div className="max-w-3xl mx-auto px-6 py-4">
              <div className="flex items-center justify-between">
                {STEPS.map((s, i) => (
                  <React.Fragment key={s.num}>
                    <div className="flex flex-col items-center gap-1">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                        step > s.num
                          ? 'bg-burnt-rose text-white'
                          : step === s.num
                          ? 'bg-dark-brown text-white' :'bg-muted text-foreground/40'
                      }`}>
                        {step > s.num ? '✓' : s.num}
                      </div>
                      <span className={`text-[10px] tracking-wide hidden sm:block transition-colors duration-300 ${
                        step === s.num ? 'text-dark-brown font-semibold' : 'text-foreground/40'
                      }`}>
                        {s.label}
                      </span>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className={`flex-1 h-px mx-2 transition-colors duration-300 ${
                        step > s.num ? 'bg-burnt-rose' : 'bg-border'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* Step content */}
          <div className="max-w-3xl mx-auto px-6 py-10 pb-24">
            <div className="animate-slide-up" key={step}>
              {step === 1 && (
                <AgendarStepCalendar
                  selectedServices={state.selectedServices}
                  selectedDate={state.selectedDate}
                  onSelect={date => update({ selectedDate: date, selectedTime: '' })}
                  onNext={goNext}
                  onBack={goBack}
                />
              )}
              {step === 2 && (
                <AgendarStepTimeSlots
                  selectedServices={state.selectedServices}
                  selectedDate={state.selectedDate}
                  selectedTime={state.selectedTime}
                  onSelect={time => update({ selectedTime: time })}
                  onNext={goNext}
                  onBack={goBack}
                />
              )}
              {step === 3 && (
                <AgendarStepCustomer
                  customerName={state.customerName}
                  customerWhatsapp={state.customerWhatsapp}
                  customerNotes={state.customerNotes}
                  onSubmit={(name, whatsapp, notes) => {
                    update({ customerName: name, customerWhatsapp: whatsapp, customerNotes: notes });
                    goNext();
                  }}
                  onBack={goBack}
                />
              )}
              {step === 4 && (
                <AgendarStepSummary
                  agendarState={state}
                  onConfirm={setConfirmedBooking}
                  onBack={goBack}
                />
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export default function BookingSection({ preSelectedIds }: { preSelectedIds?: string[] }) {
  return (
    <Suspense fallback={
      <section id="agendar" className="min-h-[400px] bg-ivory flex items-center justify-center">
        <div className="text-center">
          <p className="font-serif text-2xl text-dark-brown">A carregar...</p>
        </div>
      </section>
    }>
      <BookingSectionInner preSelectedIds={preSelectedIds} />
    </Suspense>
  );
}
